import {
  Box,
  Button,
  CircularProgress,
  Modal,
  Stack,
  Typography,
} from '@mui/material'
import { useCallback, useRef, useState } from 'react'
import { Fade, IconButton, Icons } from 'utoniq-ui'

import { ItemSelectBoxFragment, Maybe } from '@/clients/utoniq-core/schema'
import { BottomSafeArea } from '@/components/layouts'
import { ConsoleBaseLayout } from '@/components/layouts/ConsoleBaseLayout'
import {
  FullscreenModal,
  ScrollFetcher,
} from '@/components/organisms/functionals'
import { ArtistThemeProvider } from '@/components/styles'
import { useMessage, useTranslation } from '@/core'
import { Cell, CellFrame, Organism } from '@/core/architecture'

import { ItemSelectBox, ItemSelectBoxSkeleton } from './ItemSelectBox'
import { ItemSelectForm, ItemSelectFormProps } from './ItemSelectForm'

export type ItemSelectModalProps = {
  artistId: string
  open: boolean
  onClose: () => void
  loading: boolean
  hasNextPage: boolean
  addingItem: boolean
  values: string[]
  onFetchMore: () => Promise<void>
  items: Maybe<{ edges: { node: ItemSelectBoxFragment }[] }>
  onSearch: ItemSelectFormProps['onSubmit']
  onSelect: (items: string) => Promise<void>
  walletConnecting: boolean
  nftIssuing: boolean
  isNft: boolean
}

export const ItemSelectModal: Organism<ItemSelectModalProps> = ({
  artistId,
  open,
  values,
  onClose,
  loading,
  hasNextPage,
  addingItem,
  onFetchMore,
  items,
  onSearch,
  onSelect,
  nftIssuing,
  isNft,
}) => {
  const { t } = useTranslation('consoleCollectionCreate')
  const { showError, showMessage } = useMessage()
  const [itemSelected, setItemSelected] = useState<string>('')
  const [openItemAddingModal, setOpenItemAddingModal] = useState(false)
  const modalRef = useRef<HTMLDivElement>(null)

  const addItemToCollection = useCallback(async () => {
    setOpenItemAddingModal(true)
    return onSelect(itemSelected)
      .then(() => {
        showMessage(t('editMessage'), 'success')
      })
      .catch(e => {
        showError(e)
      })
      .finally(() => {
        setOpenItemAddingModal(false)
        setItemSelected('')
      })
  }, [itemSelected, onSelect, showError, showMessage, t])

  return (
    <FullscreenModal open={open}>
      <Fade>
        <ArtistThemeProvider artistId={artistId}>
          <ConsoleBaseLayout
            ref={modalRef}
            customNavigation={
              itemSelected ? (
                <FixedBottomCell
                  onSelect={() => addItemToCollection()}
                  loading={addingItem}
                />
              ) : null
            }
            margin
            header={{
              left: (
                <IconButton.Back
                  edge="start"
                  size="medium"
                  color="white"
                  sx={{
                    color: 'white',
                    '.MuiSvgIcon-root': {
                      color: 'inherit',
                    },
                  }}
                  onClick={() => {
                    onClose()
                    setItemSelected('')
                  }}
                />
              ),
              title: t('itemSelection'),
            }}
          >
            <ItemSelectForm onSubmit={onSearch} />
            <ScrollFetcher
              nodes={items?.edges?.map(({ node }) => (
                <ItemSelectBox
                  key={node.id}
                  item={node}
                  checked={[...values, itemSelected].includes(node.id)}
                  onSelect={itemId => setItemSelected(itemId)}
                  showIcon
                />
              ))}
              emptyMessage={t('emptyItemSelect')}
              loading={loading}
              hasNextPage={hasNextPage}
              onFetchMore={onFetchMore}
              loadingComponent={
                <Stack spacing={5} sx={{ pb: 2 }}>
                  {Array.from(Array(4).keys()).map(key => (
                    <ItemSelectBoxSkeleton key={key} />
                  ))}
                </Stack>
              }
              modalRef={modalRef}
            />

            <ItemCreatingModal
              open={openItemAddingModal}
              addingItem={addingItem}
              nftIssuing={nftIssuing}
              isNft={isNft}
            />
          </ConsoleBaseLayout>
        </ArtistThemeProvider>
      </Fade>
    </FullscreenModal>
  )
}

type FixedBottomCellProps = {
  onSelect: () => void
  loading: boolean
}
const FixedBottomCell: Cell<FixedBottomCellProps> = ({ onSelect, loading }) => {
  const { t } = useTranslation('consoleCollectionCreate')
  return (
    <BottomSafeArea sx={{ bgcolor: 'grey.50' }}>
      <CellFrame sx={{ py: 4, bgcolor: 'grey.50', textAlign: 'center' }}>
        <Button
          size="large"
          sx={{ minWidth: 244 }}
          onClick={onSelect}
          loading={loading}
          disabled={loading}
        >
          {t('addSelectedItem')}
        </Button>
      </CellFrame>
    </BottomSafeArea>
  )
}

type ItemCreatingModalProps = {
  open: boolean
  isNft: boolean
  addingItem: boolean
  nftIssuing: boolean
}
const ItemCreatingModal: Cell<ItemCreatingModalProps> = ({
  open,
  isNft,
  addingItem,
  nftIssuing,
}) => {
  const { t } = useTranslation('consoleItemCreate')
  return (
    <Modal open={open}>
      <Box
        id="modal-inner"
        sx={{
          width: 280,
          borderRadius: 3,
          px: 5,
          py: 7,
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'grey.100',
          outline: 'none',
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}
      >
        <Typography variant="subtitle1" sx={{ mb: 4 }}>
          {t('itemOrdering')}
        </Typography>
        <Stack flexDirection="row" alignItems="center" sx={{ ml: 8, mb: 4 }}>
          <Box
            sx={{
              height: 40,
              width: 40,
              display: 'grid',
              placeItems: 'center',
            }}
          >
            {addingItem ? (
              <CircularProgress size={30} />
            ) : (
              <Icons.RoundCheck fontSize="large" />
            )}
          </Box>
          <Stack alignItems="start" sx={{ ml: 3 }}>
            {isNft && (
              <Typography variant="subtitle2" color="secondary">
                STEP1
              </Typography>
            )}
            <Typography variant="subtitle1">{t('step1Description')}</Typography>
          </Stack>
        </Stack>
        {isNft && (
          <Stack flexDirection="row" alignItems="center" sx={{ ml: 8, mb: 5 }}>
            <Box
              sx={{
                height: 40,
                width: 40,
                display: 'grid',
                placeItems: 'center',
              }}
            >
              {addingItem || nftIssuing ? (
                <CircularProgress size={30} />
              ) : (
                <Icons.RoundCheck fontSize="large" />
              )}
            </Box>
            <Stack alignItems="start" sx={{ ml: 3 }}>
              <Typography variant="subtitle2" color="secondary">
                STEP2
              </Typography>
              <Typography variant="subtitle1">
                {t('step2Description')}
              </Typography>
            </Stack>
          </Stack>
        )}
      </Box>
    </Modal>
  )
}
